<template>
  <platform-modal
    v-model="showModal"
    :title="modalTitle"
    icon="email"
    :width="800"
    :saving="sending"
    :confirm-disabled="selectedRecipientIds.length === 0"
    @confirm="sendReport"
    @close="$emit('close')"
  >
    <p class="text-body-2 grey--text text--darken-2 mb-4">
      {{ $t('analysis.generateReport.reportDocuments.sendReportModal.description') }}
    </p>
    <v-row class="text-caption grey--text text--darken-1 mb-2" no-gutters align="center">
      <v-col cols="1"></v-col>
      <v-col :cols="showVariantColumn ? 5 : 7"></v-col>
      <v-col v-if="showVariantColumn" cols="2" class="text-center">
        {{ $t('analysis.generateReport.variant') }}
      </v-col>
      <v-col cols="3" class="text-center">
        {{ $t('analysis.generateReport.reportDocuments.sendReportModal.lastSent') }}
      </v-col>
      <v-col cols="1"></v-col>
    </v-row>
    <v-row
      v-for="recipient in eligibleRecipients"
      :key="recipient.id"
      no-gutters
      align="center"
      class="py-2"
    >
      <v-col cols="1">
        <platform-avatar
          :entity="recipient.individual"
          entity-type="individual"
          :size="60"
        />
      </v-col>
      <v-col :cols="showVariantColumn ? 5 : 7">
        <div class="pl-4">
          <div class="text-body-1 mb-1">{{ getFullName(recipient.individual) }}</div>
          <div class="text-caption">
            {{ $t(`shared.individual.roles.${recipient.individual.role}`) }}
          </div>
          <div class="text-body-2">
            {{ recipient.email || $t('analysis.generateReport.reportDocuments.sendReportModal.noEmail') }}
            <v-icon v-if="!recipient.email" color="warning" small class="ml-1">mdi-alert</v-icon>
          </div>
        </div>
      </v-col>
      <v-col v-if="showVariantColumn" cols="2" class="text-caption text-center text-uppercase">
        {{ getRecipientVariantDisplay(recipient) }}
      </v-col>
      <v-col cols="3" class="text-caption text-center">
        <span :class="getLastSentClass(recipient.sendStatus)">
          {{ formatLastSent(recipient.lastSentAt, recipient.sendStatus) }}
        </span>
      </v-col>
      <v-col cols="1" class="text-center">
        <v-checkbox
          v-model="selectedRecipientIds"
          :value="recipient.id"
          hide-details
          class="mt-0 pt-0"
        />
      </v-col>
    </v-row>
    <v-alert
      v-if="eligibleRecipients.length === 0"
      type="info"
      text
      class="mt-2"
    >
      {{ $t('analysis.generateReport.reportDocuments.sendReportModal.noRecipients') }}
    </v-alert>
  </platform-modal>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import PlatformModal from '@/components/PlatformModal.vue'
import PlatformAvatar from '@/components/shared/PlatformAvatar.vue'
import { PlatformReportDocument } from '@betterboards/shared/types/Report'
import { ReportsIndividual } from '@betterboards/shared/types/Individual'
import { ReportSendStatus, UserType } from '@/API'
import { getFullName } from '@betterboards/shared/helpers/entities/Individual/getFullName'
import { GenericReportIndividualId, ReportDocumentType } from '@betterboards/shared/helpers/entities/Report/index'
import { NullVariantCodeValue } from '@/helpers/questionnaire'
import API from '@/services/API'
import dayjs from 'dayjs'
import { LongDateTimeDisplayFormat } from '@/components/PlatformDateDisplay.vue'

interface RecipientIndividual {
  id: string
  email?: string
  individual: ReportsIndividual
  lastSentAt?: string | null
  sendStatus?: ReportSendStatus | null
}

export default Vue.extend({
  name: 'SendReportModal',
  components: {
    PlatformModal,
    PlatformAvatar
  },
  props: {
    reportDocument: {
      type: Object as PropType<PlatformReportDocument | null>,
      required: false,
      default: null
    },
    individuals: {
      type: Array as PropType<ReportsIndividual[]>,
      required: true
    },
    isBulkIndividualSend: {
      type: Boolean,
      default: false
    },
    bulkSendVariantCode: {
      type: String,
      required: false
    },
    surveyGroupId: {
      type: String,
      required: false
    },
    showVariantColumn: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      showModal: true,
      selectedRecipientIds: [] as string[],
      sending: false
    }
  },
  computed: {
    modalTitle (): string {
      const reportTypeText = this.$t(`report.types.${this.reportType}`) as string
      return this.$t('analysis.generateReport.reportDocuments.sendReportModal.title', [reportTypeText]) as string
    },
    reportType (): ReportDocumentType {
      if (this.isBulkIndividualSend) {
        return ReportDocumentType.Individual
      }
      const targetIndividualId = this.reportDocument?.targetIndividualId
      if (!targetIndividualId) {
        return ReportDocumentType.Collective
      }
      if (targetIndividualId === GenericReportIndividualId) {
        return ReportDocumentType.Generic
      }
      return ReportDocumentType.Individual
    },
    eligibleRecipients (): RecipientIndividual[] {
      const recipients: RecipientIndividual[] = []

      // Bulk individual send - all individuals can receive their own report
      if (this.isBulkIndividualSend) {
        for (const ind of this.individuals) {
          recipients.push(this.buildRecipient(ind))
        }
        return recipients
      }

      const targetId: string | null | undefined = this.reportDocument?.targetIndividualId

      switch (this.reportType) {
        case ReportDocumentType.Individual:
          if (targetId) {
            const ind = this.individuals.find(i => i.id === targetId)
            if (ind) {
              recipients.push(this.buildRecipient(ind))
            }
          }
          break

        case ReportDocumentType.Generic:
          // All participants can receive the generic report
          for (const ind of this.individuals) {
            recipients.push(this.buildRecipient(ind))
          }
          break

        case ReportDocumentType.Collective:
          // Only Chair and Company Secretary can receive the collective report
          for (const ind of this.individuals) {
            if (ind.role === UserType.Chair || ind.role === UserType.CompanySecretary) {
              recipients.push(this.buildRecipient(ind))
            }
          }
          break
      }

      return recipients
    },
    parsedSendRecipientsMap (): Record<string, { sendStatus?: ReportSendStatus }> | null {
      const sendRecipients = this.reportDocument?.sendRecipients
      if (!sendRecipients) {
        return null
      }
      try {
        return JSON.parse(sendRecipients) as Record<string, { sendStatus?: ReportSendStatus }>
      } catch (err) {
        console.warn('Failed to parse sendRecipients JSON', err)
        return null
      }
    }
  },
  mounted () {
    this.selectedRecipientIds = this.eligibleRecipients
      .filter(r => !!r.email)
      .map(r => r.id)
  },
  methods: {
    getFullName,
    getRecipientVariantDisplay (recipient: RecipientIndividual): string {
      // Mirror backend logic: explicit variant code takes precedence, otherwise use individual's survey language
      if (this.bulkSendVariantCode && this.bulkSendVariantCode !== NullVariantCodeValue) {
        return this.bulkSendVariantCode.toUpperCase()
      }
      if (!this.isBulkIndividualSend && this.reportDocument?.variantCode) {
        return this.reportDocument.variantCode.toUpperCase()
      }
      const individualVariant = recipient.individual.variantCode
      if (individualVariant && individualVariant !== NullVariantCodeValue) {
        return individualVariant.toUpperCase()
      }
      return ''
    },
    buildRecipient (reportIndividual: ReportsIndividual): RecipientIndividual {
      let lastSentAt: string | null | undefined
      let sendStatus: ReportSendStatus | null | undefined

      if (this.isBulkIndividualSend) {
        // For bulk sends, find the most recent lastSentAt from individual's reports
        const sentReports = reportIndividual.reports?.filter(r => r.lastSentAt) ?? []
        if (sentReports.length > 0) {
          const mostRecentReport = sentReports.sort((a, b) => {
            const aTime = a.lastSentAt ? new Date(a.lastSentAt).getTime() : 0
            const bTime = b.lastSentAt ? new Date(b.lastSentAt).getTime() : 0
            return bTime - aTime
          })[0]
          lastSentAt = mostRecentReport.lastSentAt
          sendStatus = this.parseSendRecipientsStatus(mostRecentReport.sendRecipients, reportIndividual.id)
        }
      } else {
        lastSentAt = this.reportDocument?.lastSentAt
        sendStatus = this.parsedSendRecipientsMap?.[reportIndividual.id]?.sendStatus
      }

      return {
        id: reportIndividual.id,
        email: reportIndividual.email,
        individual: reportIndividual,
        lastSentAt,
        sendStatus
      }
    },
    parseSendRecipientsStatus (sendRecipients: string | null | undefined, individualId: string): ReportSendStatus | undefined {
      if (!sendRecipients) {
        return undefined
      }
      try {
        const recipientsMap = JSON.parse(sendRecipients) as Record<string, { sendStatus?: ReportSendStatus }>
        return recipientsMap[individualId]?.sendStatus
      } catch (err) {
        console.warn('Failed to parse sendRecipients JSON', err)
        return undefined
      }
    },
    formatLastSent (lastSentAt: string | null | undefined, sendStatus?: ReportSendStatus | null): string {
      if (!lastSentAt) {
        if (sendStatus === ReportSendStatus.FAILED) {
          return this.$t('analysis.generateReport.reportDocuments.sendReportModal.neverSentFailed') as string
        }
        return this.$t('analysis.generateReport.reportDocuments.sendReportModal.neverSent') as string
      }
      const formattedDate = dayjs(lastSentAt).format(LongDateTimeDisplayFormat)
      if (sendStatus === ReportSendStatus.FAILED) {
        return this.$t('analysis.generateReport.reportDocuments.sendReportModal.failedToSendAt', [formattedDate]) as string
      }
      return formattedDate
    },
    getLastSentClass (sendStatus?: ReportSendStatus | null): string {
      if (sendStatus === ReportSendStatus.FAILED) {
        return 'error--text'
      }
      return 'grey--text text--darken-1'
    },
    async sendReport (): Promise<void> {
      if (this.selectedRecipientIds.length === 0) {
        return
      }

      this.sending = true

      try {
        const surveyGroupId = this.isBulkIndividualSend
          ? this.surveyGroupId
          : this.reportDocument?.surveyGroupId

        if (!surveyGroupId) {
          console.error('Missing surveyGroupId for send report request')
          return
        }

        const body: { surveyGroupId: string, reportDocumentId?: string, recipientIndividualIds: string[], variantCode?: string } = {
          surveyGroupId,
          recipientIndividualIds: this.selectedRecipientIds
        }

        if (this.reportType !== ReportDocumentType.Individual && !this.isBulkIndividualSend) {
          body.reportDocumentId = this.reportDocument?.id
        }

        if (this.isBulkIndividualSend && this.bulkSendVariantCode) {
          body.variantCode = this.bulkSendVariantCode
        }

        if (this.reportType === ReportDocumentType.Individual && !this.isBulkIndividualSend && this.reportDocument?.variantCode) {
          body.variantCode = this.reportDocument.variantCode
        }

        const response = await API.post('backendfunctions', '/report/group/send-email', { body })

        if (response?.status === 'queued') {
          const queuedCount = response.queuedCount ?? 0
          const failedCount = response.failedCount ?? 0

          if (failedCount > 0 && queuedCount > 0) {
            this.$toasted.info(
              this.$t('analysis.generateReport.reportDocuments.sendReportModal.sendPartial', [queuedCount, failedCount]) as string
            )
          } else if (failedCount > 0) {
            this.$toasted.error(
              this.$t('analysis.generateReport.reportDocuments.sendReportModal.sendAllFailed') as string
            )
          } else {
            this.$toasted.success(
              this.$t('analysis.generateReport.reportDocuments.sendReportModal.sendSuccess', [queuedCount]) as string
            )
          }

          this.$emit('sent', { queuedCount, failedCount })
          this.showModal = false
        }
      } catch (err: any) {
        console.error('Failed to send report:', err)
        this.$toasted.error(
          this.$t('analysis.generateReport.reportDocuments.sendReportModal.sendError') as string
        )
      } finally {
        this.sending = false
      }
    }
  }
})
</script>
